import React, { createContext, useContext, useState, useEffect } from 'react';

interface Company {
  id: string;
  name: string;
  color?: string;
}

interface UserCompaniesContextType {
  userCompanies: Company[];
  getCompanyName: (companyId: string) => string;
  getCompanyColor: (companyId: string) => string | undefined;
}

const UserCompaniesContext = createContext<UserCompaniesContextType | null>(null);

export const useUserCompanies = () => {
  const context = useContext(UserCompaniesContext);
  if (!context) {
    throw new Error('useUserCompanies must be used within UserCompaniesProvider');
  }
  return context;
};

interface UserCompaniesProviderProps {
  children: React.ReactNode;
  initialCompanies?: Company[];
}

export const UserCompaniesProvider: React.FC<UserCompaniesProviderProps> = ({
  children,
  initialCompanies = []
}) => {
  // Initialize from props (passed from shell via Module Federation)
  const [userCompanies, setUserCompanies] = useState<Company[]>(initialCompanies);

  // Sync state when initialCompanies prop changes (e.g., when shell finishes loading)
  // Compare by stringified IDs to avoid unnecessary updates from reference changes
  const initialCompanyIds = initialCompanies.map(c => c.id).join(',');
  useEffect(() => {
    if (initialCompanies.length > 0) {
      setUserCompanies(initialCompanies);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCompanyIds]);

  useEffect(() => {
    const handleUpdate = (event: CustomEvent<Company[]>) => {
      setUserCompanies(event.detail);
    };

    // Listen for updates from shell app (e.g., company switch)
    window.addEventListener('user-companies-updated', handleUpdate as EventListener);

    return () => {
      window.removeEventListener('user-companies-updated', handleUpdate as EventListener);
    };
  }, []);

  const getCompanyName = (companyId: string): string => {
    return userCompanies.find(c => c.id === companyId)?.name || '';
  };

  const getCompanyColor = (companyId: string): string | undefined => {
    return userCompanies.find(c => c.id === companyId)?.color;
  };

  return (
    <UserCompaniesContext.Provider value={{ userCompanies, getCompanyName, getCompanyColor }}>
      {children}
    </UserCompaniesContext.Provider>
  );
};
